import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
	CircularProgress,
} from "@mui/material";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import {
	linkWorkOrderDrawings,
	unLinkWorkOrderDrawings,
} from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import type { Drawing } from "../../../../models/WorkOrderDrawing";
import { DrawingTable } from "./drawings-tables";
import { DocumentsCatalogFreshness } from "@/shared/catalogs/components/CatalogFreshnessControls";

interface Props {
	searchDrawings: Function;
	show: boolean;
	isLoading: boolean;
	onHide: (shouldUpdate: boolean) => void;
	WorkOrderId: number | null;
	setShowLinkResourcesToAssetDialog: Function;
}

export const DrawingPicker: React.FC<Props> = ({
	searchDrawings,
	show,
	onHide,
	WorkOrderId,
	isLoading,
	setShowLinkResourcesToAssetDialog,
}) => {
	const {
		state: { listAllDrawings, workOrderDrawingsList },
		actions: { setWorkOrderDrawingToAdd, setListAllDrawings },
	} = useWorkOrderModalsContext();

	const [selectedDrawings, setSelectedDrawings] = useState<Drawing[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);

	useEffect(() => {
		if (show) {
			searchDrawings();
		}
	}, [show]);

	useEffect(() => {
		if (show) {
			setSelectedDrawings(workOrderDrawingsList);
		}
	}, [show]);

	const handleSelectionChanged = (newSelectedDrawings: Drawing[]) => {
		setSelectedDrawings(newSelectedDrawings);
	};

	const handleSave = async () => {
		setLoadingSave(true);
		const drawingsToAdd = selectedDrawings.filter(
			(drawing) =>
				!workOrderDrawingsList.some(
					(wdDrawing) =>
						wdDrawing.DocumentId === drawing.DocumentId ||
						wdDrawing.DocumentName === drawing.DocumentName,
				),
		);

		const drawingsToRemove = workOrderDrawingsList.filter(
			(drawing) =>
				!selectedDrawings.some(
					(sdDrawing) =>
						sdDrawing.DocumentId === drawing.DocumentId ||
						sdDrawing.DocumentName === drawing.DocumentName,
				),
		);

		let needsUpdate = false;

		try {
			if (drawingsToAdd.length > 0) {
				const newDrawings = drawingsToAdd.map((drawing) => ({
					DocumentName: drawing.DocumentName ?? "",
					IsAssetResource: false,
				}));
				setWorkOrderDrawingToAdd(drawingsToAdd);
				await linkWorkOrderDrawings(WorkOrderId as number, newDrawings);
				setShowLinkResourcesToAssetDialog(true);
				setSuccessMessage("Drawings added successfully");
				needsUpdate = true;
			}

			if (drawingsToRemove.length > 0) {
				for (let i = 0; i < drawingsToRemove.length; i++) {
					const drawing = drawingsToRemove[i];
					const drawingToRemove = [
						{
							DocumentName: drawing.DocumentName ?? "",
							IsAssetResource: drawing.IsAssetResource,
						},
					];
					const removeResult = await unLinkWorkOrderDrawings(
						WorkOrderId as number,
						drawingToRemove,
					);
					if (!removeResult.ok) {
						setError(removeResult.message);
						return;
					}
				}
				setSuccessMessage("Drawings removed successfully");
				needsUpdate = true;
			}

			if (needsUpdate) {
				onHide(true);
			} else {
				onHide(false);
			}
		} catch (error: any) {
			setError(error.message || "An error occurred");
		} finally {
			setLoadingSave(false);
		}
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						DOCUMENTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<DrawingTable
						allDrawings={listAllDrawings}
						workOrderDrawings={selectedDrawings}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
					<DocumentsCatalogFreshness
						onReloaded={setListAllDrawings}
						onError={setError}
					/>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => onHide(false)}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						startIcon={loadingSave && <CircularProgress size="1rem" />}
						disabled={loadingSave}
						variant="contained"
						color="primary"
						onClick={handleSave}
						sx={{ minWidth: 120 }}
					>
						SAVE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
